import { sum } from "es-toolkit";
import { Label, Pie, PieChart } from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { formatUSD } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";

import { EarningsCategories } from "./constants";

interface EarningsPieChartProps {
  earningsData?: {
    [k: string]: number;
  };
}

export const EarningsPieChart = ({
  earningsData = {},
}: EarningsPieChartProps) => {
  const chartData = Object.entries(EarningsCategories).map(
    ([key, category]) => {
      return {
        ...category,
        name: key,
        amount: (earningsData?.[key] ?? 0) / 100,
        fill: `var(--${category.color.replace("text-", "")})`,
      };
    },
  );

  const totalEarningsUsd = sum(Object.values(earningsData)) / 100;

  if (totalEarningsUsd === 0) {
    return (
      <div
        className="
          flex aspect-square h-full items-center self-center justify-self-center
          rounded-full border-24 border-skeleton text-center
        "
      >
        No earnings in this period yet
      </div>
    );
  }

  return (
    <div className="size-full">
      <ChartContainer
        config={EarningsCategories}
        className="relative top-[-13%] left-[-13%] mx-auto size-[126%]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name) => (
                  <>
                    <span>{name}</span>
                    <span>{formatUSD(value as number)}</span>
                  </>
                )}
              />
            }
          />

          <Pie
            data={chartData}
            dataKey="amount"
            nameKey="label"
            innerRadius={60}
            strokeWidth={5}
            isAnimationActive={true}
          >
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground font-mono text-2xl"
                      >
                        {totalEarningsUsd >= 1000
                          ? `$${abbreviateNumber(totalEarningsUsd)}`
                          : formatUSD(totalEarningsUsd)}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 24}
                        className="fill-muted"
                      >
                        Total
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  );
};
